import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowSquareOut,
  Check,
  PaintBrush,
  SignOut,
  Trash,
  UploadSimple,
  UserCircle,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, Badge } from '@/components/ui';
import { useAuth } from '@/lib/hooks/useAuth';
import { portfolioApi } from '@/lib/api/portfolio';
import { tenantApi } from '@/lib/api/tenant';
import { queryKeys } from '@/lib/api/queryKeys';
import { portfolioLabel, publicPortfolioUrl } from '@/lib/config';
import { cn } from '@/lib/utils/cn';

export function SettingsPage() {
  const { user, signOut } = useAuth();
  const portfolioQuery = useQuery({
    queryKey: queryKeys.portfolio,
    queryFn: () => portfolioApi.get(),
    staleTime: 60_000,
  });
  const tenantQuery = useQuery({
    queryKey: queryKeys.tenant,
    queryFn: () => tenantApi.get(),
    staleTime: 60_000,
  });
  const customDomainQuery = useQuery({
    queryKey: queryKeys.customDomain,
    queryFn: () => tenantApi.getCustomDomain(),
    staleTime: 60_000,
  });
  const portfolio = portfolioQuery.data;
  const tenant = tenantQuery.data;
  const customDomain = customDomainQuery.data;
  const isPublished = Boolean(portfolio?.publishedAt);

  return (
    <div className="mx-auto max-w-3xl space-y-10">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          Workspace
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-2 text-sm text-ink-muted">
          Manage your account and portfolio-wide preferences.
        </p>
      </header>

      <Card
        title="Profile"
        description="Shown on your live portfolio and in your account menu."
        icon={<UserCircle size={16} />}
      >
        <div className="flex items-start gap-5">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-dashed border-line bg-rail text-ink-faint">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="h-full w-full rounded-xl object-cover"
              />
            ) : (
              <UploadSimple size={20} />
            )}
          </div>
          <div className="flex-1 space-y-3">
            <Row label="Name" value={user?.name ?? '—'} />
            <Row label="Email" value={user?.email ?? '—'} muted />
            <Row label="Headline" value={user?.headline ?? 'Not set'} muted={!user?.headline} />
            <Row label="Location" value={user?.location ?? 'Not set'} muted={!user?.location} />
          </div>
        </div>
        <p className="mt-4 rounded-lg border border-dashed border-line bg-board p-3 text-xs text-ink-muted">
          Editing profile fields is handled inside the Hero and Contact sections of the editor.
        </p>
      </Card>

      <Card
        title="Portfolio"
        description="Your address on the web and publish status."
        icon={<ArrowSquareOut size={16} />}
      >
        <div className="space-y-3">
          <Row
            label="Subdomain"
            value={tenant ? portfolioLabel(tenant.subdomain) : 'Loading…'}
          />
          <Row
            label="Custom domain"
            value={customDomain?.domain ?? 'Not connected'}
            muted={!customDomain?.domain}
          />
          <div className="flex items-center justify-between py-2">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
              Status
            </span>
            <Badge tone={isPublished ? 'success' : 'soft'} leadingDot>
              {isPublished ? 'published' : 'draft'}
            </Badge>
          </div>
        </div>
        <div className="mt-5 flex gap-2 border-t border-line pt-5">
          <Link
            to="/app/domains"
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink hover:bg-rail"
          >
            Manage domains
          </Link>
          {tenant ? (
            <a
              href={publicPortfolioUrl(tenant.subdomain)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink hover:bg-rail"
            >
              Open live <ArrowSquareOut size={12} />
            </a>
          ) : null}
        </div>
      </Card>

      <Card
        title="Appearance"
        description="Quick jump to template and theme pickers."
        icon={<PaintBrush size={16} />}
      >
        <div className="grid grid-cols-2 gap-3">
          <Row label="Template" value={portfolio?.template ?? '—'} />
          <Row label="Theme" value={portfolio?.theme ?? '—'} />
        </div>
        <div className="mt-5 border-t border-line pt-5">
          <Link
            to="/app/templates"
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink hover:bg-rail"
          >
            Change template <ArrowSquareOut size={12} />
          </Link>
        </div>
      </Card>

      <Card title="Account" description="Session and data controls." icon={<SignOut size={16} />}>
        <div className="flex items-center justify-between gap-3 py-2">
          <div>
            <p className="text-sm font-medium text-ink">Sign out</p>
            <p className="mt-0.5 text-xs text-ink-muted">End this session on this device.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => signOut()}>
            Sign out
          </Button>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50/40 p-4">
          <div>
            <p className="text-sm font-medium text-red-900">Delete portfolio</p>
            <p className="mt-0.5 text-xs text-red-900/70">
              Permanently remove your site, data, and subdomain.
            </p>
          </div>
          <DeleteDialog subdomain={tenant?.subdomain ?? ''} />
        </div>
      </Card>
    </div>
  );
}

function Card({
  title,
  description,
  icon,
  children,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-line bg-board p-6">
      <header className="mb-5 flex items-start gap-3 border-b border-line pb-5">
        <span className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-canvas text-ink-muted">
          {icon}
        </span>
        <div>
          <h2 className="font-display text-base font-semibold tracking-tight">{title}</h2>
          <p className="mt-0.5 text-xs text-ink-muted">{description}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
        {label}
      </span>
      <span className={cn('text-sm', muted ? 'text-ink-faint' : 'text-ink')}>{value}</span>
    </div>
  );
}

function DeleteDialog({ subdomain }: { subdomain: string }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const canConfirm = confirm.trim() === subdomain && subdomain.length > 0;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-board px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
        >
          <Trash size={12} /> Delete
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-line bg-board p-6 shadow-float">
          <Dialog.Title className="font-display text-lg font-semibold tracking-tight">
            Delete portfolio?
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-ink-muted">
            This permanently removes your site and frees the subdomain{' '}
            <span className="font-mono text-ink">{subdomain || '—'}</span>. This cannot be undone.
          </Dialog.Description>
          <div className="mt-5 space-y-2">
            <label className="block font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
              Type the subdomain to confirm
            </label>
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={subdomain}
              className="w-full rounded-md border border-line bg-canvas px-3 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-ink focus:outline-none"
            />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink hover:bg-rail"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              disabled={!canConfirm}
              onClick={() => {
                toast.success('Deletion queued', {
                  description: 'Your portfolio will be removed shortly.',
                  icon: <Check size={14} />,
                });
                setOpen(false);
                setConfirm('');
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              <Trash size={12} /> Delete permanently
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
